'use client';
import { FC, useState, useEffect, useRef } from 'react';
import { Typography, Box, Button, LinearProgress, Alert } from '@mui/material';
import { ContentCopy, OpenInNew } from '@mui/icons-material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FolderShareLink } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { useSnackbar } from '@/hooks/useSnackbar';

interface DownloadFolderDialogProps {
  open: boolean;
  folderName: string;
  initialLinkResult: FolderShareLink;
  onClose: () => void;
  onPollForUpdates?: () => Promise<FolderShareLink>;
}

/**
 * Dialog for sharing folder links with progress tracking
 */
export const DownloadFolderDialog: FC<DownloadFolderDialogProps> = ({
  open,
  folderName,
  initialLinkResult,
  onClose,
  onPollForUpdates,
}) => {
  const { showSnackbar } = useSnackbar();
  // State only needed for polling updates (progress, done status)
  // Initial value comes from prop - component remounts when link changes (via key prop)
  const [linkResult, setLinkResult] = useState<FolderShareLink>(initialLinkResult);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const canceledRef = useRef(false);

  // Polling logic - similar to old LinkStatusUpdater
  useEffect(() => {
    if (!open || !onPollForUpdates) {
      return;
    }

    // If already done, no need to poll
    if (linkResult.done) {
      return;
    }

    // Reset canceled flag when starting to poll
    canceledRef.current = false;

    // Start polling
    const poll = async () => {
      if (canceledRef.current) {
        return;
      }

      try {
        const result = await onPollForUpdates();
        setLinkResult(result);

        // Continue polling if not done
        if (!result.done && !canceledRef.current) {
          pollingIntervalRef.current = setTimeout(poll, 3000);
        }
      } catch (error) {
        log.error('[ShareFolderDialog] Error polling for updates:', error);
        if (!canceledRef.current) {
          showSnackbar('Error checking link status', 'error');
        }
      }
    };

    // Start initial poll after 3 seconds
    pollingIntervalRef.current = setTimeout(poll, 3000);

    // Cleanup on unmount or close
    return () => {
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      canceledRef.current = true;
    };
  }, [open, linkResult.done, onPollForUpdates, showSnackbar]);

  const handleClose = () => {
    // Reset state when dialog closes
    // Only needed if same dialog reopens (same key = no remount)
    // If link changes, component remounts and useState initializes fresh
    setLinkResult(initialLinkResult);
    canceledRef.current = true;
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    onClose();
  };

  const handleCopyLink = async () => {
    if (!linkResult.link) {
      return;
    }

    try {
      await navigator.clipboard.writeText(linkResult.link);
      showSnackbar('Link copied to clipboard', 'success');
    } catch (error) {
      log.error('[ShareFolderDialog] Error copying link:', error);
      showSnackbar('Failed to copy link', 'error');
    }
  };

  const handleOpenLink = () => {
    if (linkResult.link) {
      window.open(linkResult.link, '_blank');
    }
  };

  const isDataReady = linkResult.done ?? true;
  const link = linkResult.link || '';
  const percentDone = linkResult.percentDone ?? 0;

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title={`Share ${folderName} files`}
      showDefaultActions={true}
      cancelText="Close"
      submitText=""
      onSubmit={() => {}}
      submitDisabled={true}
      maxWidth="sm"
      hasUnsavedChanges={false}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Link Display */}
        {link ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Share link:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
              }}
            >
              {link}
            </Typography>
          </Box>
        ) : null}

        {/* Progress Information */}
        {!isDataReady ? (
          <>
            <Alert severity="info">
              <Typography variant="body2">
                A SharePoint folder has been created. Your requested data is being copied now, this may take some time.
              </Typography>
            </Alert>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Progress: {percentDone}%
              </Typography>
              <LinearProgress variant="determinate" value={percentDone} />
            </Box>
          </>
        ) : null}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          {link ? (
            <>
              <Button
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={handleCopyLink}
                title={link}
                sx={{ flex: 1 }}
              >
                Copy Link
              </Button>
              <Button variant="outlined" startIcon={<OpenInNew />} onClick={handleOpenLink} sx={{ flex: 1 }}>
                Go to SharePoint folder
              </Button>
            </>
          ) : null}
        </Box>
      </Box>
    </StandardDialog>
  );
};
